/* @vite-ignore */
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { importProvidersFrom, inject, provideAppInitializer } from "@angular/core";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withPreloading,
} from "@angular/router";
import {
  ApolloClientOptions,
  ApolloLink,
  InMemoryCache,
  split
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { WebSocketLink } from "@apollo/client/link/ws";
import { getMainDefinition } from "@apollo/client/utilities";
import { AcceptLanguageHeaderInterceptor } from "@app/core/interceptors/accept-language-header.interceptor";
import { ConfigService } from "@app/core/services/config.service";
import { GameSettingsService } from "@app/core/services/game-settings.service";
import { Apollo, provideApollo } from "apollo-angular";
import { HttpLink } from "apollo-angular/http";
import { appRoutes } from "./app/ap.routes";
import { AppComponent } from "./app/app.component";
import { AuthInterceptor } from "./app/core/interceptors/auth.interceptor";
import { BASE_PATH, ChallengEUService, Configuration } from "./app/shared/rest-api";

export function createApollo(): ApolloClientOptions<any> {
  console.debug("Creating Apollo Client...");

  const configService = inject(ConfigService); // Inject ConfigService

  const uri = configService.config()?.ChallengeEUAPIBaseUrl || 'error'; // Use config or fallback
  const wsUri = uri.replace(/^http/, 'ws'); // Convert HTTP to WebSocket URI
  console.debug("GraphQL URI's:", { uri, wsUri });

  const httpLink = inject(HttpLink);

  const basic = setContext((_operation, _context) => ({
    headers: {
      // Accept: "charset=utf-8",
    },
  }));

  const http = ApolloLink.from([
    basic,
    httpLink.create({
      uri,
      withCredentials: false,
    }),
  ]);

  const ws = new WebSocketLink({
    uri: wsUri,
    options: {
      reconnect: true,
      lazy: true,
      connectionCallback: (error: any) => {
        if (error) {
          console.error("error during web socket setup", error);
        } else {
          // eslint-disable-next-line no-console
          console.debug("web socket setup completed");
          // registerListener();
        }
      },
    },
  });

  const cache = new InMemoryCache({
    addTypename: false,
  });

  const link = split(
    // split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    ws,
    http
  );

  return {
    link,
    cache,
    defaultOptions: {
      query: {
        fetchPolicy: "no-cache",
        errorPolicy: "all",
      },
    },
  };
}
const BOOTSTRAP_URL = '/api/config';

async function loadConfigAndInitApp(): Promise<void> {
  const configService = inject(ConfigService);
  const httpClient = inject(HttpClient);
  const gameSettingsService = inject(GameSettingsService);
  return fetch(BOOTSTRAP_URL).then((response) => response.json()).then(async (config) => {
    console.debug("Configuration loaded:", config);
    configService.setConfig(config);
    const basePath = configService.config()?.UmbracoApiBaseUrl || 'http://localhost';
    const configuration = new Configuration({ basePath });
    const challengEUService = new ChallengEUService(httpClient, basePath, configuration);
    try {
      const settings = await challengEUService.apiChallengeuGet().toPromise();
      gameSettingsService.gameSettings.set(settings);
      return Promise.resolve();
    } catch (error) {
      gameSettingsService.gameSettings.set({});
    }
  }).catch((error) => {
    console.error("Failed to load configuration:", error);
    throw error; // Re-throw to prevent application from bootstrapping
  });
}
bootstrapApplication(AppComponent, {
  providers: [
    ConfigService,
    GameSettingsService,
    provideAppInitializer(loadConfigAndInitApp),
    {
      provide: BASE_PATH,
      useFactory: () => inject(ConfigService).config().UmbracoApiBaseUrl,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AcceptLanguageHeaderInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
    Apollo,
    provideApollo(createApollo),
    importProvidersFrom(BrowserModule),
    provideRouter(
      appRoutes,
      withPreloading(PreloadAllModules),
      withComponentInputBinding()
      // withDebugTracing(),
    ),
    provideAnimations(),
  ],
}).catch((err) => console.error(err));
